import { useEffect, useState, type CSSProperties } from 'react'
import { fetchAccountAvatarBlob } from '../lib/api'
import type { MailAccount } from '../lib/api'
import {
  avatarFgForBg,
  needsAvatarOutline,
  sanitizeAvatarColor,
} from '../lib/avatarContrast'

type AccountLike =
  | Pick<MailAccount, 'id' | 'provider' | 'has_avatar'>
  | { id?: string; provider?: string; has_avatar?: boolean }
  | null
  | undefined

type Props = {
  account?: AccountLike
  color: string
  initial: string
  size?: number
  className?: string
  title?: string
  style?: CSSProperties
  /** Bump after upload/delete so photo reloads */
  revision?: number
}

const LOGO_PAD: Record<string, string> = {
  gmail: '#ffffff',
  google: '#ffffff',
  microsoft: '#ffffff',
  outlook: '#ffffff',
  yahoo: '#6001d2',
  gmx: '#1c449b',
  imap: '#2b3040',
  custom: '#2b3040',
}

const SAFE_FALLBACK = '#6d28d9'

/** Self-hosted brand marks only (CSP: same-origin). */
export function providerLogoSrc(provider: string | undefined | null): string | null {
  const p = (provider || '').toLowerCase()
  if (p === 'gmail' || p === 'google') return '/providers/gmail.svg'
  if (p === 'microsoft' || p === 'outlook') return '/providers/microsoft.svg'
  if (p === 'yahoo') return '/providers/yahoo.svg'
  if (p === 'gmx') return '/providers/gmx.svg'
  if (p === 'custom' || p === 'imap') return '/providers/imap.svg'
  return null
}

export function providerLogoBg(provider: string | undefined | null): string {
  const p = (provider || '').toLowerCase()
  return LOGO_PAD[p] || '#ffffff'
}

/**
 * Render chain (ADR-013): authenticated photo blob → provider logo → colored initial.
 * Never uses third-party CDN from the client (CSP img-src self/data/blob).
 * Initial bg is contrast-sanitized — never invisible white-on-white.
 */
export function AccountAvatar({
  account,
  color,
  initial,
  size = 30,
  className = '',
  title,
  style,
  revision = 0,
}: Props) {
  const [photoUrl, setPhotoUrl] = useState<string | null>(null)
  const [photoFailed, setPhotoFailed] = useState(false)
  const [logoFailed, setLogoFailed] = useState(false)
  const logo = providerLogoSrc(account?.provider)
  const accountId = account?.id

  useEffect(() => {
    setPhotoUrl(null)
    setPhotoFailed(false)
  }, [accountId, revision, account?.has_avatar])

  useEffect(() => {
    if (!accountId || photoFailed) return
    // Skip GET when API already said no avatar — avoids console 404 spam.
    if (account?.has_avatar === false && revision === 0) {
      setPhotoFailed(true)
      return
    }
    let objectUrl: string | null = null
    let cancelled = false
    void (async () => {
      const blob = await fetchAccountAvatarBlob(accountId)
      if (cancelled) return
      if (!blob) {
        setPhotoFailed(true)
        return
      }
      const created = URL.createObjectURL(blob)
      if (cancelled) {
        URL.revokeObjectURL(created)
        return
      }
      objectUrl = created
      setPhotoUrl(created)
    })()
    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [accountId, account?.has_avatar, photoFailed, revision])

  const showPhoto = Boolean(photoUrl) && !photoFailed
  const showLogo = !showPhoto && Boolean(logo) && !logoFailed
  const safeInitialBg = sanitizeAvatarColor(color, SAFE_FALLBACK)
  const bg = showPhoto
    ? 'transparent'
    : showLogo
      ? providerLogoBg(account?.provider)
      : safeInitialBg
  const fg = showPhoto || showLogo ? undefined : avatarFgForBg(safeInitialBg)
  const outline =
    !showPhoto && (showLogo || needsAvatarOutline(safeInitialBg))
      ? '1px solid rgba(0,0,0,0.18)'
      : undefined

  return (
    <span
      className={`rd-dot${showLogo ? ' rd-dot-logo' : ''}${showPhoto ? ' rd-dot-photo' : ''} ${className}`.trim()}
      title={title}
      style={{
        width: size,
        height: size,
        background: bg,
        color: fg,
        boxShadow: outline ? `inset 0 0 0 1px rgba(0,0,0,0.12)` : undefined,
        border: outline,
        fontSize: Math.max(11, Math.round(size * 0.43)),
        ...style,
      }}
    >
      {showPhoto ? (
        <img src={photoUrl!} alt="" onError={() => setPhotoFailed(true)} />
      ) : showLogo ? (
        <img src={logo!} alt="" onError={() => setLogoFailed(true)} />
      ) : (
        (initial || '?').slice(0, 2)
      )}
    </span>
  )
}
